/* eslint-disable max-len */
import React from 'react';
import styled from 'styled-components';
import { theme, toRem } from 'design';

interface SortLabelProps {
  label: string;
  isLoading?: boolean;
  isSorted?: boolean;
  isSortedDesc?: boolean;
  onClick?: () => void;
  className?: string;
  dataCy?: string;
}

const Layout = styled.button`
  position: relative;
  display: flex;
  flex-grow: 0;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(11)};
  line-height: 100%;
  text-align: left;
  white-space: nowrap;
  color: ${theme.color.neutral[900]};
  cursor: pointer;
`;

interface IconWrapperProps {
  isSorted?: boolean;
  isSortedDesc?: boolean;
}

const IconWrapper = styled.span<IconWrapperProps>`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  margin-left: ${toRem(8.25)};
`;

const COLOR_ACTIVE = theme.color.neutral[900];
const COLOR_INACTIVE = theme.color.neutral[300];
const STROKE_ACTIVE = 0.8;
const STROKE_INACTIVE = 0.6;

const SortLabel: React.FC<SortLabelProps> = ({ label, isLoading, isSorted, isSortedDesc, onClick, className, dataCy, ...rest }: SortLabelProps) => {
  const sortedASC = isSorted && !isSortedDesc;
  const sortedDSC = isSorted && isSortedDesc;
  const ariaLabel = sortedASC ? `Sorted ascending by ${label}` : sortedDSC ? `Sorted descending by ${label}` : `Click to sort by ${label}.`;

  return (
    <Layout aria-label={ariaLabel} className={className} onClick={onClick} data-cy={dataCy} {...rest}>
      {isLoading ? null : (
        <>
          {label}

          <IconWrapper>
            <svg width="9" height="13" viewBox="0 0 9 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.90115 4.77615C7.76935 4.90795 7.55565 4.90795 7.42385 4.77615L4.625 1.9773L1.82615 4.77615C1.69435 4.90795 1.48065 4.90795 1.34885 4.77615C1.21705 4.64435 1.21705 4.43065 1.34885 4.29885L4.38635 1.26135C4.51815 1.12955 4.73185 1.12955 4.86365 1.26135L7.90115 4.29885C8.03295 4.43065 8.03295 4.64435 7.90115 4.77615Z"
                fill={sortedASC ? COLOR_ACTIVE : COLOR_INACTIVE}
                stroke={sortedASC ? COLOR_ACTIVE : COLOR_INACTIVE}
                strokeWidth={sortedASC ? STROKE_ACTIVE : STROKE_INACTIVE}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.34885 8.22385C1.48065 8.09205 1.69435 8.09205 1.82615 8.22385L4.625 11.0227L7.42385 8.22385C7.55565 8.09205 7.76935 8.09205 7.90115 8.22385C8.03295 8.35565 8.03295 8.56935 7.90115 8.70115L4.86365 11.7386C4.73185 11.8705 4.51815 11.8705 4.38635 11.7386L1.34885 8.70115C1.21705 8.56935 1.21705 8.35565 1.34885 8.22385Z"
                fill={sortedDSC ? COLOR_ACTIVE : COLOR_INACTIVE}
                stroke={sortedDSC ? COLOR_ACTIVE : COLOR_INACTIVE}
                strokeWidth={sortedDSC ? STROKE_ACTIVE : STROKE_INACTIVE}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </IconWrapper>
        </>
      )}
    </Layout>
  );
};

export default SortLabel;
